import {
  getPendingSaasHandoffBootstrap,
  getPendingSaasHandoffIntent,
} from './saasHandoffIntent';

const SAAS_META_CAPI_STATE_KEY = 'run:saasMetaCapiState';
let currentOwnerAccessProof: string | undefined;

type PendingSaasMetaCapiState = {
  source: 'saas';
  entryPath: string;
  sessionId?: string;
  targetChatId?: string;
  targetUsername?: string;
  firstMessageEventId?: string;
  firstMessageTrackedAt?: number;
  createdAt: number;
};

function readStoredState(): PendingSaasMetaCapiState | undefined {
  try {
    const raw = window.sessionStorage.getItem(SAAS_META_CAPI_STATE_KEY);
    return raw ? JSON.parse(raw) as PendingSaasMetaCapiState : undefined;
  } catch {
    return undefined;
  }
}

function writeStoredState(state: PendingSaasMetaCapiState) {
  try {
    window.sessionStorage.setItem(SAAS_META_CAPI_STATE_KEY, JSON.stringify(state));
  } catch {
    // fail-close: analytics state must not perturb the real handoff lane
  }
}

function normalizeText(value?: string | null) {
  return value?.trim() || undefined;
}

function resolveCurrentSaasHandoffContext(search = window.location.search) {
  const searchParams = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const intent = getPendingSaasHandoffIntent();
  const bootstrapHint = getPendingSaasHandoffBootstrap();

  const source = intent?.source || searchParams.get('run_source') || bootstrapHint?.source;
  if (source !== 'saas') {
    return undefined;
  }

  const entryPath =
    intent?.entryPath
    || bootstrapHint?.entryPath
    || searchParams.get('run_entry_path')
    || 'customer-service';
  const sessionId =
    intent?.sessionId
    || bootstrapHint?.sessionId
    || searchParams.get('run_session_id')
    || undefined;
  const ownerAccessToken =
    intent?.ownerAccessToken
    || bootstrapHint?.ownerAccessToken
    || searchParams.get('ownerAccessToken')
    || undefined;
  return {
    source: 'saas' as const,
    entryPath,
    sessionId,
    ownerAccessToken,
  };
}

function buildFirstMessageEventId(params: {
  sessionId?: string;
  chatId: string;
}) {
  return `sage_first_message:${params.sessionId || 'no_session'}:${params.chatId}`.slice(0, 255);
}

export function rememberSaasMetaCapiTarget(params: {
  chatId?: string;
  username?: string;
  search?: string;
}) {
  const context = resolveCurrentSaasHandoffContext(params.search);
  if (!context) return;

  writeStoredState({
    source: context.source,
    entryPath: context.entryPath,
    ...(context.sessionId ? { sessionId: context.sessionId } : {}),
    ...(normalizeText(params.chatId) ? { targetChatId: normalizeText(params.chatId) } : {}),
    ...(normalizeText(params.username)
      ? { targetUsername: normalizeText(params.username)?.toLowerCase() }
      : {}),
    createdAt: Date.now(),
  });
  currentOwnerAccessProof = context.ownerAccessToken;
}

export async function sendSaasMetaCapiFirstMessageEvent(params: {
  chatId: string;
  currentUserId?: string;
  username?: string;
  fetchFn?: typeof fetch;
}) {
  const state = readStoredState();
  if (!state || state.firstMessageTrackedAt) {
    return false;
  }

  const normalizedChatId = normalizeText(params.chatId);
  if (!normalizedChatId) {
    return false;
  }

  const normalizedUsername = normalizeText(params.username)?.toLowerCase();
  const matchesTarget = state.targetChatId
    ? state.targetChatId === normalizedChatId
    : Boolean(state.targetUsername && normalizedUsername && state.targetUsername === normalizedUsername);
  if (!matchesTarget) {
    return false;
  }

  if (!currentOwnerAccessProof) {
    return false;
  }
  const fetchFn = params.fetchFn ?? window.fetch.bind(window);
  const response = await fetchFn('/api/run/saas-meta-capi', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      canonicalEvent: 'first_message',
      sessionId: state.sessionId,
      entryPath: state.entryPath,
      ownerAccessToken: currentOwnerAccessProof,
      payload: {
        source: 'sage-chat',
        targetChatId: normalizedChatId,
      },
    }),
  });

  if (!response.ok) {
    return false;
  }

  writeStoredState({
    ...state,
    targetChatId: normalizedChatId,
    firstMessageEventId: buildFirstMessageEventId({
      sessionId: state.sessionId,
      chatId: normalizedChatId,
    }),
    firstMessageTrackedAt: Date.now(),
  });
  currentOwnerAccessProof = undefined;
  return true;
}

export function resetSaasMetaCapiState() {
  try {
    window.sessionStorage.removeItem(SAAS_META_CAPI_STATE_KEY);
  } catch {
    // ignore
  }
  currentOwnerAccessProof = undefined;
}

export function clearSaasMetaCapiOwnerProofForTests() {
  currentOwnerAccessProof = undefined;
}
